import type { CommandOf, CommandResult } from '@/core/app/commands';
import type { DialogueId, ItemId, NpcId } from '@/core/content/ids';
import type { ContentRegistry } from '@/core/content/registry';
import type { DialogueDef, DialogueEffect } from '@/core/content/schemas/progressionDefs';
import type { DomainEvent } from '@/core/events/types';
import type { GameSave } from '@/core/persistence/schema/v1';
import { evaluateAll, evaluateCondition } from './conditions';
import { addFriendship, ensureDay, npcState } from './friendship';
import type { HandlerEnv } from './placement/handlers';
import { acquireItem } from './rewards';
import { createRng, hash32 } from './rng';
import { pickVisitDialogue, visitSeenFlag } from './visits';

export type DialogueNode = DialogueDef['nodes'][number];

/**
 * DialogueRunner (content-schemas §5.2). Pure helpers the UI uses to walk a dialogue, plus the commands that
 * apply effects. Effects apply once per save (`dlg:<id>:<node>[:c<i>]`), so re-reading never pays twice.
 */
export function visibleNode(
  content: ContentRegistry,
  s: GameSave,
  dialogueId: DialogueId,
  nodeId: string | null | undefined,
): DialogueNode | null {
  const def = content.dialogues.get(dialogueId);
  if (!def) return null;
  let id = nodeId;
  // Bounded walk: a dialogue never has more hidden nodes in a row than it has nodes.
  for (let guard = 0; id != null && guard <= def.nodes.length; guard++) {
    const node = def.nodes.find((n) => n.id === id);
    if (!node) return null;
    if (!node.condition || evaluateCondition(content, s, node.condition).ok) return node;
    id = node.next;
  }
  return null;
}

export function entryNode(
  content: ContentRegistry,
  s: GameSave,
  dialogueId: DialogueId,
): DialogueNode | null {
  return visibleNode(content, s, dialogueId, content.dialogues.get(dialogueId)?.nodes[0]?.id);
}

/** The node id that follows `node` (after picking `choiceIndex`, when it has choices). */
export function nextNodeId(node: DialogueNode, choiceIndex?: number): string | null {
  if (node.choices) return node.choices[choiceIndex ?? 0]?.next ?? null;
  return node.next ?? null;
}

function owned(s: GameSave, itemId: ItemId): number {
  if (itemId.startsWith('prod_')) return s.inventory.productStacks[itemId as `prod_${string}`] ?? 0;
  return Object.values(s.inventory.instances).filter((i) => i.itemId === itemId).length;
}

function applyEffects(
  draft: GameSave,
  env: HandlerEnv,
  dialogueId: DialogueId,
  nodeId: string,
  key: string,
  effects: readonly DialogueEffect[] | undefined,
): DomainEvent[] {
  if (!effects?.length || draft.dialogue.appliedEffectKeys.includes(key)) return [];
  draft.dialogue.appliedEffectKeys.push(key);
  const events: DomainEvent[] = [];
  for (const effect of effects) {
    switch (effect.type) {
      case 'grantItem': {
        // "Make sure the player owns it" (economy §5): only the missing copies are granted.
        const missing = effect.qty - owned(draft, effect.itemId);
        if (missing > 0) events.push(acquireItem(draft, effect.itemId, missing, 'dialogue', key));
        break;
      }
      case 'friendship':
        events.push(
          ...addFriendship(draft, env.content, effect.npcId, 'dialogue', env.nowWallMs, effect.amount),
        );
        break;
      case 'setFlag':
        draft.flags[effect.flag] = effect.value;
        break;
      case 'unlockChapter':
        if (!draft.story.unlockedChapters.includes(effect.chapterId)) {
          draft.story.unlockedChapters.push(effect.chapterId);
          events.push({ type: 'CHAPTER_UNLOCKED', chapterId: effect.chapterId });
        }
        break;
    }
    events.push({ type: 'DIALOGUE_EFFECT_APPLIED', dialogueId, nodeId, effect });
  }
  return events;
}

/** The player leaves a node (tap to continue, or picks a choice). */
export function advanceDialogue(
  draft: GameSave,
  cmd: CommandOf<'advanceDialogue'>,
  env: HandlerEnv,
): CommandResult {
  const def = env.content.dialogues.get(cmd.dialogueId);
  const node = def?.nodes.find((n) => n.id === cmd.nodeId);
  if (!def || !node) return { ok: false, error: 'NOT_FOUND' };
  if (node.choices && (cmd.choiceIndex === undefined || !node.choices[cmd.choiceIndex]))
    return { ok: false, error: 'INVALID_ARGUMENT' };
  if (!node.choices && cmd.choiceIndex !== undefined) return { ok: false, error: 'INVALID_ARGUMENT' };
  if (node.condition && !evaluateCondition(env.content, draft, node.condition).ok)
    return { ok: false, error: 'CONDITIONS_NOT_MET' };
  const base = `dlg:${def.id}:${node.id}`;
  const events = applyEffects(draft, env, def.id, node.id, base, node.effects);
  if (cmd.choiceIndex !== undefined) {
    const choice = node.choices?.[cmd.choiceIndex];
    events.push(
      ...applyEffects(draft, env, def.id, node.id, `${base}:c${String(cmd.choiceIndex)}`, choice?.effects),
    );
  }
  const next = visibleNode(env.content, draft, def.id, nextNodeId(node, cmd.choiceIndex));
  if (!next) {
    if (!draft.dialogue.seenDialogueIds.includes(def.id)) draft.dialogue.seenDialogueIds.push(def.id);
    events.push({ type: 'DIALOGUE_ENDED', dialogueId: def.id });
  }
  return { ok: true, events };
}

/** Pick what a neighbour says (content-schemas §5.1 talk): conditions, unseen `once` first, priority, no repeats. */
export function pickTalk(content: ContentRegistry, s: GameSave, npcId: NpcId): DialogueId | null {
  const def = content.npcs.get(npcId);
  if (!def) return null;
  const open = def.talk.filter(
    (t) =>
      evaluateAll(content, s, t.conditions).ok &&
      !(t.once && s.dialogue.seenDialogueIds.includes(t.dialogueId)),
  );
  if (open.length === 0) return null;
  const once = open.filter((t) => t.once);
  const pool = once.length > 0 ? once : open;
  const top = Math.max(...pool.map((t) => t.priority));
  let best = pool.filter((t) => t.priority === top);
  const last = s.npcs[npcId]?.lastDialogueId;
  if (best.length > 1) best = best.filter((t) => t.dialogueId !== last);
  // Seeded per save, neighbour and talk count: replays are deterministic, every talk differs.
  const rng = createRng(hash32(s.rngSeed, 'talk', npcId, s.npcs[npcId]?.talkCount ?? 0));
  return rng.pick(best).dialogueId;
}

export function talkToNpc(draft: GameSave, cmd: CommandOf<'talkToNpc'>, env: HandlerEnv): CommandResult {
  const def = env.content.npcs.get(cmd.npcId);
  if (!def) return { ok: false, error: 'NOT_FOUND' };
  if (def.kind !== 'human') return { ok: false, error: 'INVALID_ARGUMENT' };
  // A neighbour visiting Home has their own lines (content-schemas §14.11); talking still counts as today's talk.
  const visit = cmd.initiatedBy === 'visit';
  const dialogueId = visit
    ? pickVisitDialogue(env.content, draft, cmd.npcId)
    : pickTalk(env.content, draft, cmd.npcId);
  if (!dialogueId) return { ok: false, error: 'CONDITIONS_NOT_MET' };
  if (visit) draft.flags[visitSeenFlag(cmd.npcId)] = true;
  ensureDay(draft, env.nowWallMs);
  const firstToday = !draft.daily.perNpc[cmd.npcId]?.talkBonusGiven;
  const n = npcState(draft, cmd.npcId);
  n.talkCount += 1;
  n.lastDialogueId = dialogueId;
  draft.flags[def.metFlag] = true;
  return {
    ok: true,
    events: [
      {
        type: 'NPC_TALKED',
        npcId: cmd.npcId,
        dialogueId,
        firstToday,
        initiatedBy: cmd.initiatedBy ?? 'player',
      },
      ...addFriendship(draft, env.content, cmd.npcId, 'talk', env.nowWallMs),
    ],
  };
}

const CAT: NpcId = 'npc_boss_cat';
const CAT_AFTER_CAP: DialogueId = 'dlg_cat_tap_13';

/** Tapping Sếp Mèo (content-schemas §14.10): +1 friendship per pet up to the daily cap, a short "Meo." line. */
export function petCat(draft: GameSave, _cmd: CommandOf<'petCat'>, env: HandlerEnv): CommandResult {
  const def = env.content.npcs.get(CAT);
  if (!def) return { ok: false, error: 'NOT_FOUND' };
  if (!draft.flags[def.metFlag]) return { ok: false, error: 'LOCKED' };
  ensureDay(draft, env.nowWallMs);
  const friendship = addFriendship(draft, env.content, CAT, 'pet', env.nowWallMs);
  const gained = friendship.find((e) => e.type === 'FRIENDSHIP_CHANGED');
  const n = npcState(draft, CAT);
  const atCap = gained === undefined;
  const dialogueId =
    atCap && env.content.dialogues.has(CAT_AFTER_CAP) ? CAT_AFTER_CAP : pickTalk(env.content, draft, CAT);
  n.talkCount += 1;
  if (dialogueId) n.lastDialogueId = dialogueId;
  return {
    ok: true,
    events: [
      {
        type: 'CAT_PETTED',
        petsToday: draft.daily.perNpc[CAT]?.petPoints ?? 0,
        friendshipGained: gained?.type === 'FRIENDSHIP_CHANGED' ? gained.delta : 0,
        ...(dialogueId ? { dialogueId } : {}),
      },
      ...friendship,
    ],
  };
}
